import { Drawer } from '@/components';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import { isBefore2025 } from '@/features/project/_utils/project';
import { useMessage } from '@/providers';
import { type FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { FundAdditionalIncomeAndExpenditureType } from '../../../../_models/fund-additiona-income-and-expenditure';
import { useProjectDetailDrawerState } from '../../../_components/ProjectDrawerStateProvider';
import {
  useUpdateFundAdditionalIncomeAndExpenditure,
  useUpdateFundAdditionalIncomeAndExpenditurePrior,
} from '../../_hooks/fund-additional-income-and-expenditure';
import { getIncomeAndExpenditureSummaryByType } from '../../_utils/fund-income-and-expenditure-table';
import { FundAdditionalIncomeAndExpenditureForm } from './FundAdditionalIncomeAndExpenditureForm';

type Props = {
  fiscalYear: number;
  fundAdditionalIncomeAndExpenditure: FundAdditionalIncomeAndExpenditureType;
};

export const FundAdditionalIncomeAndExpenditureDrawer: FC<Props> = ({
  fiscalYear,
  fundAdditionalIncomeAndExpenditure,
}) => {
  const { t } = useTranslation();
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const { mutateAsync: updateFundAdditionalIncomeAndExpenditurePrior } =
    useUpdateFundAdditionalIncomeAndExpenditurePrior();
  const { mutateAsync: updateFundAdditionalIncomeAndExpenditure } =
    useUpdateFundAdditionalIncomeAndExpenditure();
  const { openMessage } = useMessage();
  const { devtoolState } = useDevToolsStorage();
  const isDebugMode = !!devtoolState?.isDebugMode;

  const isFundAdditionalIncomeAndExpenditureOpen =
    drawerState.open &&
    drawerState.type === 'fund-additional-income-and-expenditure';
  const isEstimatedFundAdditionalIncomeAndExpenditureOpen =
    drawerState.open &&
    drawerState.type === 'estimated-fund-additional-income-and-expenditure';
  const isEstimatedFundAdditionalIncomeAndExpenditureNextYearOpen =
    drawerState.open &&
    drawerState.type ===
      'estimated-fund-additional-income-and-expenditure-next-year';

  const handleClose = async () => {
    setDrawerState({ open: false });
  };

  const getBeginningOfTheFundBalance = useCallback(
    (
      fundAdditionalIncomeAndExpenditure?: FundAdditionalIncomeAndExpenditureType | null,
    ) => {
      if (isBefore2025({ fiscalYear }) || !fundAdditionalIncomeAndExpenditure) {
        return;
      }
      if (isEstimatedFundAdditionalIncomeAndExpenditureNextYearOpen) {
        const { value } = getIncomeAndExpenditureSummaryByType({
          fundAdditionalIncomeAndExpenditure,
          type: 'estimated-this-year',
          isDebugMode,
        });

        return {
          beginningOfTheEstimatedFundBalanceNextYear:
            typeof value === 'number' || typeof value === 'bigint'
              ? Number(value)
              : null,
        };
      }
      if (!fundAdditionalIncomeAndExpenditure.prior) {
        return;
      }

      const { value } = getIncomeAndExpenditureSummaryByType({
        fundAdditionalIncomeAndExpenditure:
          fundAdditionalIncomeAndExpenditure.prior,
        type: 'previous-year-performance',
        isDebugMode,
      });

      return {
        beginningOfTheFundBalance:
          typeof value === 'number' || typeof value === 'bigint'
            ? Number(value)
            : null,
      };
    },
    [
      fiscalYear,
      isEstimatedFundAdditionalIncomeAndExpenditureNextYearOpen,
      isDebugMode,
    ],
  );

  return (
    <Drawer
      open={
        isFundAdditionalIncomeAndExpenditureOpen ||
        isEstimatedFundAdditionalIncomeAndExpenditureOpen ||
        isEstimatedFundAdditionalIncomeAndExpenditureNextYearOpen
      }
      onClose={handleClose}
      destroyOnClose
    >
      {isFundAdditionalIncomeAndExpenditureOpen &&
        fundAdditionalIncomeAndExpenditure.prior && (
          <FundAdditionalIncomeAndExpenditureForm
            isBefore2025={isBefore2025({ fiscalYear })}
            type="previous-year-performance"
            initialValues={{
              ...fundAdditionalIncomeAndExpenditure.prior,
              ...getBeginningOfTheFundBalance(
                fundAdditionalIncomeAndExpenditure.prior,
              ),
            }}
            onSubmit={async (values) => {
              invariant(
                fundAdditionalIncomeAndExpenditure.prior,
                t('unreachable-code'),
              );

              await updateFundAdditionalIncomeAndExpenditurePrior({
                fundAdditionalIncomeAndExpenditure: {
                  ...values,
                  id: fundAdditionalIncomeAndExpenditure.prior.id,
                },
              });

              await Promise.all([
                openMessage('success', t('message-update-completed')),
                handleClose(),
              ]);
            }}
          />
        )}
      {(isEstimatedFundAdditionalIncomeAndExpenditureOpen ||
        isEstimatedFundAdditionalIncomeAndExpenditureNextYearOpen) && (
        <FundAdditionalIncomeAndExpenditureForm
          isBefore2025={isBefore2025({ fiscalYear })}
          type={
            isEstimatedFundAdditionalIncomeAndExpenditureOpen
              ? 'estimated-this-year'
              : 'estimated-next-year'
          }
          initialValues={{
            ...fundAdditionalIncomeAndExpenditure,
            ...getBeginningOfTheFundBalance(fundAdditionalIncomeAndExpenditure),
          }}
          onSubmit={async (values) => {
            await updateFundAdditionalIncomeAndExpenditure({
              projectId: drawerState.projectId,
              fundAdditionalIncomeAndExpenditure: values,
            });
            await Promise.all([
              openMessage('success', t('message-update-completed')),
              handleClose(),
            ]);
          }}
        />
      )}
    </Drawer>
  );
};
